import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import { useAuthorization } from '../../../../../hooks/useAuthorization';
import { useMonitoringSessionPolling } from '../../../../../hooks/useMonitoringSessionPolling';
import {
  createMonitoringSession,
  getMonitoringSession,
  listMonitoringSessions
} from '../../../../../services/DeployerControlMonitoringService';
import {
  resetDeployerControlMonitoring,
  selectSession,
  setActiveSessionError,
  setActiveSessionLoading,
  setActiveSessionSuccess,
  setSessionsError,
  setSessionsLoading,
  setSessionsSuccess,
  upsertSession
} from '../../../../../store/slices/deployerControlMonitoringSlice';
import { showToast } from '../../../../../store/slices/toastSlice';
import { MetricResultsAccordion } from './MetricResultsAccordion';
import { RunAnalysisButton } from './RunAnalysisButton';
import { SessionHistoryList } from './SessionHistoryList';
import { SessionScorePill } from './SessionScorePill';

export function DeployerControlMonitoring() {
  const { id: aiSystemId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { can } = useAuthorization();

  const {
    aiSystemId: cachedAiSystemId,
    sessions,
    sessionsLoading,
    selectedSessionId,
    activeSessionDetail,
    activeSessionLoading,
    activeSessionError
  } = useAppSelector((state) => state.deployerControlMonitoring);

  // This component stays mounted across route navigation between different
  // AI systems (only the :id param changes), AND gets fully unmounted and
  // remounted every time its tab is switched away from and back to (Tabs.tsx
  // only renders the active tab's content). `cachedAiSystemId` (set by
  // setSessionsSuccess, and surviving unmount since it lives in Redux) lets
  // us tell those two cases apart: only reset/re-fetch when the AI system
  // actually changed, so a plain tab switch shows what's already loaded
  // instantly instead of blanking and hitting the network every time.
  const isDifferentAiSystem = !!cachedAiSystemId && cachedAiSystemId !== aiSystemId;

  useEffect(() => {
    if (isDifferentAiSystem) {
      dispatch(resetDeployerControlMonitoring());
    }
  }, [isDifferentAiSystem, dispatch]);

  const { isStalled } = useMonitoringSessionPolling({ aiSystemId });

  useEffect(() => {
    if (!aiSystemId) return;
    if (cachedAiSystemId === aiSystemId) return; // already loaded for this system
    dispatch(setSessionsLoading(true));
    listMonitoringSessions(aiSystemId)
      .then((data) => dispatch(setSessionsSuccess({ aiSystemId, sessions: data })))
      .catch(() => dispatch(setSessionsError('Failed to load monitoring sessions')));
  }, [aiSystemId, cachedAiSystemId, dispatch]);

  const displayedSessionId = selectedSessionId ?? sessions[0]?.id ?? null;

  useEffect(() => {
    if (!aiSystemId || !displayedSessionId) return;
    // Already have this exact session's detail cached (e.g. a tab-switch
    // remount, not a real navigation) — nothing to fetch.
    if (activeSessionDetail?.id === displayedSessionId) return;
    let isActive = true;
    dispatch(setActiveSessionLoading(true));
    getMonitoringSession(aiSystemId, displayedSessionId)
      .then((detail) => {
        if (!isActive) return;
        dispatch(setActiveSessionSuccess(detail));
      })
      .catch(() => {
        if (!isActive) return;
        dispatch(setActiveSessionError('Failed to load session detail'));
      });
    return () => {
      isActive = false;
    };
    // activeSessionDetail is intentionally excluded — it's read only to decide
    // whether to skip THIS run, not to re-trigger the effect on every update
    // (which would defeat the cache-check the moment it succeeds).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiSystemId, displayedSessionId, dispatch]);

  const handleRunAnalysis = async () => {
    if (!aiSystemId) return;
    try {
      const result = await createMonitoringSession(aiSystemId);
      if ('skipped' in result) {
        dispatch(
          showToast({
            message:
              result.reason === 'no_documents'
                ? 'This AI system has no documents to analyze yet.'
                : "Documents haven't changed since the last analysis — skipping.",
            type: 'info'
          })
        );
        return;
      }

      const { id } = result;
      dispatch(
        upsertSession({
          id,
          aiSystemId,
          status: 'PENDING',
          triggerType: 'MANUAL',
          documentIds: [],
          inputDataApplicability: null,
          inputDataApplicabilityRationale: null,
          overallScorePercent: null,
          overallStatus: null,
          errorMessage: null,
          createdAt: new Date().toISOString(),
          completedAt: null
        })
      );
      dispatch(selectSession(id));
    } catch (error) {
      // A 409 (active session already exists) or any other error already
      // shows a toast via the shared axios response interceptor. Refetch the
      // list so a 409 caused by a session this page didn't know about yet
      // (e.g. a just-completed scheduled run) is reflected immediately,
      // instead of leaving the button re-triggering the same 409 forever.
      console.error('Failed to start monitoring analysis:', error);
      listMonitoringSessions(aiSystemId)
        .then((data) => dispatch(setSessionsSuccess({ aiSystemId, sessions: data })))
        .catch(() => {
          // Best-effort refresh only; the interceptor already toasted the
          // original error.
        });
    }
  };

  const hasActiveSession = sessions.some(
    (session) => session.status === 'PENDING' || session.status === 'IN_PROGRESS'
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <span className="font-inter font-medium text-base leading-6 text-[#27272A]">
          Article 26 Compliance
        </span>
        <RunAnalysisButton
          onClick={handleRunAnalysis}
          disabled={hasActiveSession && !isStalled}
          canManage={can('aiSystems.deployerMonitoring.manage')}
        />
      </div>

      {activeSessionLoading && <span className="text-sm text-gray-500">Loading…</span>}
      {activeSessionError && <span className="text-sm text-rose-700">{activeSessionError}</span>}

      <SessionHistoryList
        sessions={sessions}
        loading={sessionsLoading}
        selectedSessionId={displayedSessionId}
        onSelectSession={(sessionId) => dispatch(selectSession(sessionId))}
      />

      {/* Keep showing the previously-loaded session's content while a newly
          selected one is fetching, instead of hiding it — swapping the whole
          block out for just the "Loading…" line above collapses the page's
          height for a moment and snaps back once the fetch resolves. */}
      {activeSessionDetail && (
        <>
          {activeSessionDetail.status === 'FAILED' ? (
            // errorMessage is an internal/diagnostic string (a raw exception
            // message, sometimes with implementation details) — never render
            // it verbatim to the end user. It's still stored in the DB for
            // developers/support to inspect directly if needed.
            <span className="text-sm text-rose-700">
              Analysis failed. Please try running the analysis again.
            </span>
          ) : activeSessionDetail.status !== 'COMPLETED' ? (
            <span className="text-sm text-gray-500">
              Analysis in progress ({activeSessionDetail.status})…
            </span>
          ) : (
            <div className="flex flex-col gap-4">
              <SessionScorePill
                overallStatus={activeSessionDetail.overallStatus}
                overallScorePercent={activeSessionDetail.overallScorePercent}
              />
              <MetricResultsAccordion results={activeSessionDetail.results} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
